import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Equipement, QueueOption, SensorUpsertRequest, DEVICE_TYPE_KEYS } from '../../core/services/equipement.service';
import { EstablishmentBrief } from '../../core/session.service';

@Component({
  selector: 'app-capteur-upsert-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './capteur-upsert-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./capteur-upsert-modal.component.scss'],
})
export class CapteurUpsertModalComponent implements OnInit, OnDestroy {
  @Input() sensor: Equipement | null = null;
  @Input() queues: QueueOption[] = [];
  @Input() establishments: EstablishmentBrief[] = [];
  @Input() initialEstablishmentId: string | null = null;
  @Input() saving = false;
  @Input() saveError: string | null = null;
  @Output() save = new EventEmitter<SensorUpsertRequest>();
  @Output() close = new EventEmitter<void>();
  @Output() establishmentChange = new EventEmitter<string>();

  private readonly translate = inject(TranslateService);

  selectedEstablishmentId: string | null = null;

  get deviceTypes(): { value: string; label: string }[] {
    return Object.entries(DEVICE_TYPE_KEYS).map(([value, key]) => ({
      value,
      label: this.translate.instant('equipements.upsertModal.deviceTypes.' + key),
    }));
  }

  form: SensorUpsertRequest = {
    name: '',
    deviceEui: '',
    deviceType: 'PEOPLE_COUNTER',
    location: null,
    queueId: null,
    active: true,
  };

  errors: Partial<Record<keyof SensorUpsertRequest, string>> = {};

  get isEdit(): boolean { return this.sensor !== null; }
  get title(): string   { return this.translate.instant(this.isEdit ? 'equipements.upsertModal.titleEdit' : 'equipements.upsertModal.titleAdd'); }
  get sortedEstablishments(): EstablishmentBrief[] {
    return [...this.establishments].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }

  ngOnInit() {
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
    this.selectedEstablishmentId = this.initialEstablishmentId ?? this.establishments[0]?.id ?? null;
    if (this.sensor) {
      this.form = {
        name:       this.sensor.capteur,
        deviceEui:  this.sensor.deviceEui,
        deviceType: this.sensor.deviceTypeCode ?? 'PEOPLE_COUNTER',
        location:   this.sensor.lieu,
        queueId:    this.sensor.queueId,
        active:     this.sensor.active,
      };
    }
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  onEstablishmentChange(id: string) {
    this.selectedEstablishmentId = id;
    this.form.queueId = null;
    this.establishmentChange.emit(id);
  }

  onSubmit() {
    if (!this.validate()) return;
    this.save.emit({
      ...this.form,
      deviceEui: this.form.deviceEui.trim().toUpperCase().replace(/[^0-9A-F]/g, ''),
      name:      this.form.name.trim(),
      location:  this.form.location?.trim() || null,
      queueId:   this.form.queueId || null,
    });
  }

  private validate(): boolean {
    this.errors = {};
    if (!this.form.name.trim())
      this.errors['name'] = this.translate.instant('equipements.upsertModal.errors.nameRequired');
    const eui = this.form.deviceEui.trim().replace(/[^0-9A-Fa-f]/g, '');
    if (!eui || eui.length !== 16)
      this.errors['deviceEui'] = this.translate.instant('equipements.upsertModal.errors.devEuiInvalid');
    if (!this.form.deviceType)
      this.errors['deviceType'] = this.translate.instant('equipements.upsertModal.errors.deviceTypeRequired');
    return Object.keys(this.errors).length === 0;
  }

}
